import {
  CameraIcon,
  HomeIcon,
  IdentificationIcon,
  UserIcon,
  PhoneIcon,
  UsersIcon,
  PencilIcon,
  EyeIcon,
  TrashIcon,
  PlusIcon,
} from '@heroicons/react/24/outline'
import Modal from '../../common/Modal'

export default function ResidentFormModal({
  isOpen,
  onClose,
  selectedResident,
  handleSubmit,
  handleSubmitResident,
  register,
  errors,
  regions,
  provinces,
  municipalities,
  barangays,
  selectedRegion,
  selectedProvince,
  selectedMunicipality,
  capturedPhoto,
  setCapturedPhoto,
  openPhotoCapture,
  getResidentInitials,
  familyMembers,
  handleEditFamilyMember,
  handleViewFamilyMember,
  handleConfirmDeleteFamilyMember,
  handleAddFamilyMember,
  isDeletingFamilyMember,
  getPhotoUrl,
  isSubmitting,
  resetResidentForm,
}) {
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size='6xl'
      alignment='top'
      title={
        selectedResident ? 'Edit Resident Profile' : 'New Resident Registration'
      }
    >
      <form
        onSubmit={handleSubmit(handleSubmitResident)}
        className='space-y-8 max-h-[70vh] overflow-y-auto px-3'
      >
        <div className='grid grid-cols-1 md:grid-cols-3 gap-5'>
          {/* Photo Upload Section */}
          <div className='animate-in slide-in-from-left duration-500 delay-100'>
            <h4 className='text-lg font-medium text-gray-900 mb-2 flex items-center'>
              <CameraIcon className='w-5 h-5 mr-2 text-blue-600' />
              Profile Photo
            </h4>
            <div className='flex items-center space-x-4'>
              <div className='w-44 h-44 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center bg-gray-50 hover:bg-gray-100 transition-colors duration-200 relative'>
                {capturedPhoto ? (
                  <img
                    src={capturedPhoto}
                    alt='Profile photo'
                    className='w-full h-full object-cover rounded-lg'
                    onError={(e) => {
                      console.error('Failed to load photo:', capturedPhoto)
                      setCapturedPhoto(null)
                    }}
                  />
                ) : selectedResident ? (
                  <div className='text-center'>
                    <div className='w-20 h-20 bg-gradient-to-r from-blue-400 to-blue-600 rounded-full flex items-center justify-center text-white font-bold text-2xl mx-auto mb-2'>
                      {getResidentInitials(selectedResident)}
                    </div>
                    <p className='text-xs text-gray-500'>No photo available</p>
                  </div>
                ) : (
                  <CameraIcon className='w-8 h-8 text-gray-400' />
                )}
              </div>
              <div className='space-y-2'>
                <button
                  type='button'
                  onClick={openPhotoCapture}
                  className='px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-all duration-200 hover:scale-105 active:scale-95 flex items-center'
                >
                  <CameraIcon className='w-4 h-4 mr-2' />
                  Capture Photo
                </button>
                <p className='text-xs text-gray-500'>
                  Recommended: 2x2 ID photo format
                </p>
              </div>
            </div>
          </div>

          {/* Household Information */}
          <div className='animate-in slide-in-from-left duration-500 delay-400'>
            <h4 className='text-lg font-medium text-gray-900 mb-2 flex items-center'>
              <HomeIcon className='w-5 h-5 mr-2 text-blue-600' />
              Household Information
            </h4>
            {/* <div className="group mb-3">
                        <label className="block text-sm font-medium text-gray-700 mb-2">Household ID</label>
                        <input
                          type="text"
                          {...register('household_id')}
                          className="mt-1 block w-full border-2 border-gray-200 rounded-lg px-4 py-3 text-gray-900 placeholder-gray-500  transition-all duration-200 group-hover:border-gray-300"
                          placeholder="HH-0001"
                        />
                      </div> */}

            <div className='group'>
              <label className='block text-sm font-medium text-gray-700 mb-2'>
                Relationship to Head
              </label>
              <select
                {...register('relationship_to_head')}
                className='mt-1 block w-full border-2 border-gray-200 rounded-lg px-4 py-3 text-gray-900  transition-all duration-200 group-hover:border-gray-300'
              >
                <option value=''>Select relationship</option>
                <option value='Head'>Head of Household</option>
                <option value='Spouse'>Spouse</option>
                <option value='Son'>Son</option>
                <option value='Daughter'>Daughter</option>
                <option value='Father'>Father</option>
                <option value='Mother'>Mother</option>
                <option value='Brother'>Brother</option>
                <option value='Sister'>Sister</option>
                <option value='Grandfather'>Grandfather</option>
                <option value='Grandmother'>Grandmother</option>
                <option value='Member'>Family Member</option>
                <option value='Other'>Other</option>
              </select>
            </div>
          </div>

          {/* System Information */}
          <div className='animate-in slide-in-from-right duration-500 delay-500'>
            <h4 className='text-lg font-medium text-gray-900 mb-2 flex items-center'>
              <IdentificationIcon className='w-5 h-5 mr-2 text-blue-600' />
              Digital ID Information
            </h4>
            <div className='group'>
              <label className='block text-sm font-medium text-gray-700 mb-2'>
                Status
              </label>
              <select
                {...register('status')}
                className='mt-1 block w-full border-2 border-gray-200 rounded-lg px-4 py-3 text-gray-900  transition-all duration-200 group-hover:border-gray-300'
              >
                <option value='active'>Active</option>
                <option value='inactive'>Inactive</option>
                <option value='verified'>Verified</option>
                <option value='pending'>Pending</option>
              </select>
            </div>
          </div>
        </div>

        <div className='animate-in slide-in-from-right duration-500 delay-200'>
          <h4 className='text-lg font-medium text-gray-900 mb-2 flex items-center'>
            <UserIcon className='w-5 h-5 mr-2 text-blue-600' />
            Personal Information
          </h4>
          <div className='grid grid-cols-1 md:grid-cols-3 gap-3'>
            <div className='group'>
              <label className='block text-sm font-medium text-gray-700 mb-2'>
                First Name *
              </label>
              <input
                type='text'
                {...register('first_name', {
                  required: 'First name is required',
                })}
                className='mt-1 block w-full border-2 border-gray-200 rounded-lg px-4 py-3 text-gray-900 placeholder-gray-500 transition-all duration-200 group-hover:border-gray-300'
                placeholder='Enter first name'
              />
              {errors.first_name && (
                <p className='text-red-500 text-xs mt-2 animate-in slide-in-from-left duration-300'>
                  {errors.first_name.message}
                </p>
              )}
            </div>

            <div className='group'>
              <label className='block text-sm font-medium text-gray-700 mb-2'>
                Last Name *
              </label>
              <input
                type='text'
                {...register('last_name', {
                  required: 'Last name is required',
                })}
                className='mt-1 block w-full border-2 border-gray-200 rounded-lg px-4 py-3 text-gray-900 placeholder-gray-500  transition-all duration-200 group-hover:border-gray-300'
                placeholder='Enter last name'
              />
              {errors.last_name && (
                <p className='text-red-500 text-xs mt-2 animate-in slide-in-from-left duration-300'>
                  {errors.last_name.message}
                </p>
              )}
            </div>

            <div className='grid grid-cols-1 md:grid-cols-[70%_25%] gap-3'>
              <div className='group '>
                <label className='block  text-sm font-medium text-gray-700 mb-2'>
                  Middle Name
                </label>
                <input
                  type='text'
                  {...register('middle_name')}
                  className='mt-1 block w-full border-2 border-gray-200 rounded-lg px-4 py-3 text-gray-900 placeholder-gray-500  transition-all duration-200 group-hover:border-gray-300'
                  placeholder='Enter middle name (optional)'
                />
              </div>

              <div className='group w-full'>
                <label className='block text-sm font-medium text-gray-700 mb-2'>
                  Suffix
                </label>
                <input
                  type='text'
                  {...register('suffix')}
                  className='mt-1 block w-full border-2 border-gray-200 rounded-lg px-4 py-3 text-gray-900 placeholder-gray-500  transition-all duration-200 group-hover:border-gray-300'
                  placeholder='Jr, Sr, III, etc.'
                />
              </div>
            </div>

            <div className='group'>
              <label className='block text-sm font-medium text-gray-700 mb-2'>
                Date of Birth *
              </label>
              <input
                type='date'
                {...register('date_of_birth', {
                  required: 'Date of birth is required',
                })}
                className='mt-1 block w-full border-2 border-gray-200 rounded-lg px-4 py-3 text-gray-900  transition-all duration-200 group-hover:border-gray-300'
              />
              {errors.date_of_birth && (
                <p className='text-red-500 text-xs mt-2 animate-in slide-in-from-left duration-300'>
                  {errors.date_of_birth.message}
                </p>
              )}
            </div>

            <div className='group'>
              <label className='block text-sm font-medium text-gray-700 mb-2'>
                Birthplace *
              </label>
              <input
                type='text'
                {...register('birthplace', {
                  required: 'Birthplace is required',
                })}
                className='mt-1 block w-full border-2 border-gray-200 rounded-lg px-4 py-3 text-gray-900 placeholder-gray-500  transition-all duration-200 group-hover:border-gray-300'
                placeholder='Enter birthplace'
              />
              {errors.birthplace && (
                <p className='text-red-500 text-xs mt-2 animate-in slide-in-from-left duration-300'>
                  {errors.birthplace.message}
                </p>
              )}
            </div>

            <div className='group'>
              <label className='block text-sm font-medium text-gray-700 mb-2'>
                Citizenship *
              </label>
              <input
                type='text'
                {...register('citizenship', {
                  required: 'Citizenship is required',
                })}
                className='mt-1 block w-full border-2 border-gray-200 rounded-lg px-4 py-3 text-gray-900 placeholder-gray-500  transition-all duration-200 group-hover:border-gray-300'
                placeholder='e.g., Filipino'
                defaultValue='Filipino'
              />
              {errors.citizenship && (
                <p className='text-red-500 text-xs mt-2 animate-in slide-in-from-left duration-300'>
                  {errors.citizenship.message}
                </p>
              )}
            </div>

            <div className='group'>
              <label className='block text-sm font-medium text-gray-700 mb-2'>
                Gender *
              </label>
              <select
                {...register('gender', {
                  required: 'Gender is required',
                })}
                className='mt-1 block w-full border-2 border-gray-200 rounded-lg px-4 py-3 text-gray-900  transition-all duration-200 group-hover:border-gray-300'
              >
                <option value=''>Select gender</option>
                <option value='male'>Male</option>
                <option value='female'>Female</option>
                <option value='other'>Other</option>
              </select>
              {errors.gender && (
                <p className='text-red-500 text-xs mt-2 animate-in slide-in-from-left duration-300'>
                  {errors.gender.message}
                </p>
              )}
            </div>

            <div className='group'>
              <label className='block text-sm font-medium text-gray-700 mb-2'>
                Civil Status *
              </label>
              <select
                {...register('civil_status', {
                  required: 'Civil status is required',
                })}
                className='mt-1 block w-full border-2 border-gray-200 rounded-lg px-4 py-3 text-gray-900  transition-all duration-200 group-hover:border-gray-300'
              >
                <option value=''>Select status</option>
                <option value='single'>Single</option>
                <option value='married'>Married</option>
                <option value='divorced'>Divorced</option>
                <option value='widowed'>Widowed</option>
                <option value='separated'>Separated</option>
              </select>
              {errors.civil_status && (
                <p className='text-red-500 text-xs mt-2 animate-in slide-in-from-left duration-300'>
                  {errors.civil_status.message}
                </p>
              )}
            </div>

            <div className='group'>
              <label className='block text-sm font-medium text-gray-700 mb-2'>
                Religion
              </label>
              <input
                type='text'
                {...register('religion')}
                className='mt-1 block w-full border-2 border-gray-200 rounded-lg px-4 py-3 text-gray-900 placeholder-gray-500  transition-all duration-200 group-hover:border-gray-300'
                placeholder='Enter religion'
              />
            </div>

            <div className='group'>
              <label className='block text-sm font-medium text-gray-700 mb-2'>
                Occupation
              </label>
              <input
                type='text'
                {...register('occupation')}
                className='mt-1 block w-full border-2 border-gray-200 rounded-lg px-4 py-3 text-gray-900 placeholder-gray-500  transition-all duration-200 group-hover:border-gray-300'
                placeholder='Enter occupation'
              />
            </div>

            <div className='group'>
              <label className='block text-sm font-medium text-gray-700 mb-2'>
                Monthly Income
              </label>
              <input
                type='number'
                {...register('monthly_income')}
                className='mt-1 block w-full border-2 border-gray-200 rounded-lg px-4 py-3 text-gray-900 placeholder-gray-500  transition-all duration-200 group-hover:border-gray-300'
                placeholder='Enter monthly income'
                min='0'
                step='0.01'
              />
            </div>

            <div className='group'>
              <label className='block text-sm font-medium text-gray-700 mb-2'>
                Education Level
              </label>
              <select
                {...register('education_level')}
                className='mt-1 block w-full border-2 border-gray-200 rounded-lg px-4 py-3 text-gray-900  transition-all duration-200 group-hover:border-gray-300'
              >
                <option value='no_formal_education'>No Formal Education</option>
                <option value='elementary'>Elementary</option>
                <option value='high_school'>High School</option>
                <option value='vocational'>Vocational</option>
                <option value='college'>College</option>
                <option value='graduate'>Graduate</option>
              </select>
            </div>
          </div>
        </div>

        {/* Contact Information */}
        <div className='animate-in slide-in-from-left duration-500 delay-300'>
          <h4 className='text-lg font-medium text-gray-900 mb-2 flex items-center'>
            <PhoneIcon className='w-5 h-5 mr-2 text-blue-600' />
            Contact Information
          </h4>
          <div className='grid grid-cols-1 md:grid-cols-3 gap-3'>
            <div className='group'>
              <label className='block text-sm font-medium text-gray-700 mb-2'>
                Phone Number
              </label>
              <input
                type='tel'
                {...register('phone_number')}
                className='mt-1 block w-full border-2 border-gray-200 rounded-lg px-4 py-3 text-gray-900 placeholder-gray-500  transition-all duration-200 group-hover:border-gray-300'
                placeholder='+63 912 345 6789'
              />
            </div>

            <div className='group'>
              <label className='block text-sm font-medium text-gray-700 mb-2'>
                Email Address
              </label>
              <input
                type='email'
                {...register('email')}
                className='mt-1 block w-full border-2 border-gray-200 rounded-lg px-4 py-3 text-gray-900 placeholder-gray-500  transition-all duration-200 group-hover:border-gray-300'
                placeholder='example@email.com'
              />
            </div>

            <div className='group'>
              <label className='block text-sm font-medium text-gray-700 mb-2'>
                Emergency Contact Name
              </label>
              <input
                type='text'
                {...register('emergency_contact_name')}
                className='mt-1 block w-full border-2 border-gray-200 rounded-lg px-4 py-3 text-gray-900 placeholder-gray-500  transition-all duration-200 group-hover:border-gray-300'
                placeholder='Full name of emergency contact'
              />
            </div>

            <div className='group'>
              <label className='block text-sm font-medium text-gray-700 mb-2'>
                Emergency Contact Number
              </label>
              <input
                type='tel'
                {...register('emergency_contact_number')}
                className='mt-1 block w-full border-2 border-gray-200 rounded-lg px-4 py-3 text-gray-900 placeholder-gray-500  transition-all duration-200 group-hover:border-gray-300'
                placeholder='+63 917 888 9999'
              />
            </div>

            <div className='group'>
              <label className='block text-sm font-medium text-gray-700 mb-2'>
                House Number
              </label>
              <input
                type='text'
                {...register('house_number')}
                className='mt-1 block w-full border-2 border-gray-200 rounded-lg px-4 py-3 text-gray-900 placeholder-gray-500  transition-all duration-200 group-hover:border-gray-300'
                placeholder='123'
              />
            </div>

            <div className='group'>
              <label className='block text-sm font-medium text-gray-700 mb-2'>
                Street
              </label>
              <input
                type='text'
                {...register('street')}
                className='mt-1 block w-full border-2 border-gray-200 rounded-lg px-4 py-3 text-gray-900 placeholder-gray-500  transition-all duration-200 group-hover:border-gray-300'
                placeholder='Main Street'
              />
            </div>

            <div className='group'>
              <label className='block text-sm font-medium text-gray-700 mb-2'>
                Region *
              </label>
              {/* <input
                        type="text"
                        {...register('region', { required: 'Region is required' })}
                        className="mt-1 block w-full border-2 border-gray-200 rounded-lg px-4 py-3 text-gray-900 placeholder-gray-500  transition-all duration-200 group-hover:border-gray-300"
                        placeholder="Sample Region"
                        defaultValue="Bulacan"
                      />
                      {errors.province && (
                        <p className="text-red-500 text-xs mt-2 animate-in slide-in-from-left duration-300">{errors.province.message}</p>
                      )} */}
              <select
                {...register('region', {
                  required: 'Region is required',
                })}
                className='w-full rounded-xl border border-gray-300 px-4 py-3 focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100'
              >
                <option value=''>Select Region</option>
                {regions.map((region) => (
                  <option key={region.code} value={region.code}>
                    {region.name}
                  </option>
                ))}
              </select>
            </div>

            <div className='group'>
              <label className='block text-sm font-medium text-gray-700 mb-2'>
                Province *
              </label>
              {/* <input
                        type="text"
                        {...register('province', { required: 'Province is required' })}
                        className="mt-1 block w-full border-2 border-gray-200 rounded-lg px-4 py-3 text-gray-900 placeholder-gray-500  transition-all duration-200 group-hover:border-gray-300"
                        placeholder="Sample Province"
                        defaultValue="Bulacan"
                      />
                      {errors.province && (
                        <p className="text-red-500 text-xs mt-2 animate-in slide-in-from-left duration-300">{errors.province.message}</p>
                      )} */}
              <select
                {...register('province', {
                  required: 'Province is required',
                })}
                className='w-full rounded-xl border border-gray-300 px-4 py-3 focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100'
                disabled={!selectedRegion}
              >
                <option value=''>
                  {selectedRegion ? 'Select Province' : 'Select Region First'}
                </option>
                {provinces.map((province) => (
                  <option key={province.code} value={province.code}>
                    {province.name}
                  </option>
                ))}
              </select>
            </div>

            <div className='group'>
              <label className='block text-sm font-medium text-gray-700 mb-2'>
                Municipality *
              </label>
              {/* <input
                        type="text"
                        {...register('municipality', { required: 'Municipality is required' })}
                        className="mt-1 block w-full border-2 border-gray-200 rounded-lg px-4 py-3 text-gray-900 placeholder-gray-500  transition-all duration-200 group-hover:border-gray-300"
                        placeholder="Sample City"
                        defaultValue="Paombong"
                      />
                      {errors.municipality && (
                        <p className="text-red-500 text-xs mt-2 animate-in slide-in-from-left duration-300">{errors.municipality.message}</p>
                      )} */}
              <select
                {...register('municipality', {
                  required: 'Municipality is required',
                })}
                className='w-full rounded-xl border border-gray-300 px-4 py-3 focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100'
                disabled={!selectedProvince}
              >
                <option value=''>
                  {selectedProvince
                    ? 'Select Municipality'
                    : 'Select Province First'}
                </option>
                {municipalities.map((municipality) => (
                  <option key={municipality.code} value={municipality.code}>
                    {municipality.name}
                  </option>
                ))}
              </select>
            </div>

            <div className='group'>
              <label className='block text-sm font-medium text-gray-700 mb-2'>
                Barangay *
              </label>
              {/* <input
                        type="text"
                        {...register('barangay', { required: 'Barangay is required' })}
                        className="mt-1 block w-full border-2 border-gray-200 rounded-lg px-4 py-3 text-gray-900 placeholder-gray-500  transition-all duration-200 group-hover:border-gray-300"
                        placeholder="Barangay Center"
                        defaultValue="Binakod"
                      />
                      {errors.barangay && (
                        <p className="text-red-500 text-xs mt-2 animate-in slide-in-from-left duration-300">{errors.barangay.message}</p>
                      )} */}
              <select
                {...register('barangay', {
                  required: 'Barangay is required',
                })}
                className='w-full rounded-xl border border-gray-300 px-4 py-3 focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100'
                disabled={!selectedMunicipality}
              >
                <option value=''>
                  {selectedMunicipality
                    ? 'Select Barangay'
                    : 'Select Municipality First'}
                </option>
                {barangays.map((barangay) => (
                  <option key={barangay.code} value={barangay.code}>
                    {barangay.name}
                  </option>
                ))}
              </select>
            </div>

            <div className='group'>
              <label className='block text-sm font-medium text-gray-700 mb-2'>
                Purok/Zone *
              </label>
              <input
                type='text'
                {...register('purok_zone', {
                  required: 'Purok/Zone is required',
                })}
                className='mt-1 block w-full border-2 border-gray-200 rounded-lg px-4 py-3 text-gray-900 placeholder-gray-500  transition-all duration-200 group-hover:border-gray-300'
                placeholder='Zone 1'
              />
              {errors.purok_zone && (
                <p className='text-red-500 text-xs mt-2 animate-in slide-in-from-left duration-300'>
                  {errors.purok_zone.message}
                </p>
              )}
            </div>

            <div className='group'>
              <label className='block text-sm font-medium text-gray-700 mb-2'>
                ZIP Code
              </label>
              <input
                type='text'
                {...register('zip_code')}
                className='mt-1 block w-full border-2 border-gray-200 rounded-lg px-4 py-3 text-gray-900 placeholder-gray-500  transition-all duration-200 group-hover:border-gray-300'
                placeholder='3001'
              />
            </div>

            <div className='group'>
              <label className='block text-sm font-medium text-gray-700 mb-2'>
                Length of Residency
              </label>
              <input
                type='text'
                {...register('length_of_residency')}
                className='mt-1 block w-full border-2 border-gray-200 rounded-lg px-4 py-3 text-gray-900 placeholder-gray-500  transition-all duration-200 group-hover:border-gray-300'
                placeholder='e.g., 5 years'
              />
            </div>
          </div>
        </div>

        {/* Additional Information */}
        <div className='animate-in slide-in-from-right duration-500 delay-400'>
          <h4 className='text-lg font-medium text-gray-900 mb-2 flex items-center'>
            <IdentificationIcon className='w-5 h-5 mr-2 text-blue-600' />
            Voter information
          </h4>
          <div className='grid grid-cols-1 md:grid-cols-3 gap-3'>
            <div className='group'>
              <label className='block text-sm font-medium text-gray-700 mb-2'>
                Voter Status
              </label>
              <select
                {...register('voter_status')}
                className='mt-1 block w-full border-2 border-gray-200 rounded-lg px-4 py-3 text-gray-900  transition-all duration-200 group-hover:border-gray-300'
              >
                <option value=''>Select voter status</option>
                <option value='registered'>Registered</option>
                <option value='not_registered'>Not Registered</option>
                <option value='pending'>Pending</option>
              </select>
            </div>

            <div className='group'>
              <label className='block text-sm font-medium text-gray-700 mb-2'>
                Precinct Number
              </label>
              <input
                type='text'
                {...register('precinct_number')}
                readOnly={!!selectedResident}
                className={`mt-1 block w-full border-2 border-gray-200 rounded-lg px-4 py-3 text-gray-900 placeholder-gray-500  transition-all duration-200 group-hover:border-gray-300 ${
                  selectedResident ? 'bg-gray-100 cursor-not-allowed' : ''
                }`}
                placeholder='e.g., 001A'
              />
            </div>
          </div>
        </div>

        {/* Additional Information */}
        <div className='animate-in slide-in-from-right duration-500 delay-400'>
          <h4 className='text-lg font-medium text-gray-900 mb-2 flex items-center'>
            <IdentificationIcon className='w-5 h-5 mr-2 text-blue-600' />
            Additional Information
          </h4>
          <div className='grid grid-cols-1 md:grid-cols-3 gap-3 mb-6'>
            <div className='group'>
              <label className='block text-sm font-medium text-gray-700 mb-2'>
                Indigenous Group
              </label>
              <input
                type='text'
                {...register('indigenous_group')}
                className='mt-1 block w-full border-2 border-gray-200 rounded-lg px-4 py-3 text-gray-900 placeholder-gray-500  transition-all duration-200 group-hover:border-gray-300'
                placeholder='If applicable'
              />
            </div>

            <div className='group'>
              <label className='block text-sm font-medium text-gray-700 mb-2'>
                4Ps Number
              </label>
              <input
                type='text'
                {...register('four_ps_number')}
                className='mt-1 block w-full border-2 border-gray-200 rounded-lg px-4 py-3 text-gray-900 placeholder-gray-500  transition-all duration-200 group-hover:border-gray-300'
                placeholder='Enter 4Ps number'
              />
            </div>
          </div>

          <div className='grid grid-cols-1 sm:grid-cols-2 gap-3'>
            <div className='group'>
              <label className='flex items-center p-4 border-2 border-gray-200 rounded-lg transition-all duration-200 hover:border-gray-300 hover:bg-gray-50 cursor-pointer'>
                <input
                  type='checkbox'
                  {...register('solo_parent_status')}
                  className='w-5 h-5 text-blue-600 bg-gray-100 border-2 border-gray-300 rounded focus:ring-blue-500 focus:ring-2 transition-all duration-200'
                />
                <span className='ml-3 text-sm font-medium text-gray-700'>
                  Solo Parent
                </span>
              </label>
            </div>

            <div className='group'>
              <label className='flex items-center p-4 border-2 border-gray-200 rounded-lg transition-all duration-200 hover:border-gray-300 hover:bg-gray-50 cursor-pointer'>
                <input
                  type='checkbox'
                  {...register('pwd_status')}
                  className='w-5 h-5 text-blue-600 bg-gray-100 border-2 border-gray-300 rounded focus:ring-blue-500 focus:ring-2 transition-all duration-200'
                />
                <span className='ml-3 text-sm font-medium text-gray-700'>
                  Person with Disability (PWD)
                </span>
              </label>
            </div>

            <div className='group'>
              <label className='flex items-center p-4 border-2 border-gray-200 rounded-lg transition-all duration-200 hover:border-gray-300 hover:bg-gray-50 cursor-pointer'>
                <input
                  type='checkbox'
                  {...register('senior_citizen_status')}
                  className='w-5 h-5 text-blue-600 bg-gray-100 border-2 border-gray-300 rounded focus:ring-blue-500 focus:ring-2 transition-all duration-200'
                />
                <span className='ml-3 text-sm font-medium text-gray-700'>
                  Senior Citizen
                </span>
              </label>
            </div>

            <div className='group'>
              <label className='flex items-center p-4 border-2 border-gray-200 rounded-lg transition-all duration-200 hover:border-gray-300 hover:bg-gray-50 cursor-pointer'>
                <input
                  type='checkbox'
                  {...register('illiterate_status')}
                  className='w-5 h-5 text-blue-600 bg-gray-100 border-2 border-gray-300 rounded focus:ring-blue-500 focus:ring-2 transition-all duration-200'
                />
                <span className='ml-3 text-sm font-medium text-gray-700'>
                  Illiterate
                </span>
              </label>
            </div>
          </div>
        </div>

        {/* Family Members Section */}
        <div className='bg-gray-50 border-2 border-gray-200 rounded-lg p-6 animate-in slide-in-from-left duration-500 delay-[2400ms]'>
          <h3 className='text-lg font-medium text-gray-900 mb-2 flex items-center'>
            <UsersIcon className='w-5 h-5 mr-2 text-blue-600' />
            Family Members
          </h3>

          {/* Current Family Members */}
          {familyMembers.length > 0 ? (
            <div className='space-y-3 mb-2'>
              <p className='text-xs text-gray-500 mb-2'>
                Found {familyMembers.length} family member(s)
              </p>
              {familyMembers.map((member) => (
                <div
                  key={member.id || Math.random()}
                  className='flex items-center justify-between p-3 bg-white border border-gray-200 rounded-lg'
                >
                  <div className='flex items-center space-x-3'>
                    {getPhotoUrl(member) ? (
                      <img
                        src={getPhotoUrl(member)}
                        alt={`${member.first_name} ${member.last_name}`}
                        className='h-12 w-12 rounded-full object-cover border-2 border-blue-200 hover:scale-110 transition-transform duration-200'
                        onError={(e) => {
                          e.target.style.display = 'none'
                          e.target.parentNode.querySelector(
                            '.fallback-avatar'
                          ).style.display = 'flex'
                        }}
                      />
                    ) : (
                      <div className='w-10 h-10 bg-gradient-to-r from-blue-400 to-blue-600 rounded-full flex items-center justify-center text-white font-semibold text-sm'>
                        {member.name
                          ? member.name
                              .split(' ')
                              .map((n) => n.charAt(0))
                              .join('')
                          : member.first_name && member.last_name
                          ? member.first_name.charAt(0) +
                            member.last_name.charAt(0)
                          : 'N/A'}
                      </div>
                    )}
                    <div>
                      <p className='font-medium text-gray-900'>
                        {member.name ||
                          `${member.first_name || ''} ${
                            member.last_name || ''
                          }`.trim() ||
                          'Unnamed Member'}
                      </p>
                      <p className='text-sm text-gray-600'>
                        {member.relationship || 'Unknown'} •{' '}
                        {member.age || 'N/A'} years old
                      </p>
                    </div>
                  </div>

                  <div className='flex-shrink-0'>
                    <button
                      type='button'
                      onClick={() => handleEditFamilyMember(member)}
                      className='text-yellow-600 hover:text-yellow-900 p-2 rounded-full hover:bg-yellow-50 transition-all duration-200'
                      title='Edit Family Member'
                    >
                      <PencilIcon className='h-4 w-4' />
                    </button>
                    <button
                      type='button'
                      onClick={() => handleViewFamilyMember(member)}
                      className='text-blue-600 hover:text-blue-900 p-2 rounded-full hover:bg-blue-50 transition-all duration-200'
                      title='View Family Member'
                    >
                      <EyeIcon className='h-4 w-4' />
                    </button>
                    <button
                      type='button'
                      onClick={() => handleConfirmDeleteFamilyMember(member)}
                      disabled={isDeletingFamilyMember}
                      className='text-red-600 hover:text-red-900 p-2 rounded-full hover:bg-red-50 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed'
                      title='Delete Family Member'
                    >
                      <TrashIcon className='h-4 w-4' />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className='text-center py-4 border-2 border-dashed border-gray-300 rounded-lg'>
              <UsersIcon className='mx-auto h-8 w-8 text-gray-400' />
              <p className='text-sm text-gray-500 mt-2'>
                No family members added yet
              </p>
            </div>
          )}

          {/* Add Family Member Button */}
          {selectedResident?.is_household_head && (
            <button
              type='button'
              onClick={handleAddFamilyMember}
              className='w-full p-3 mt-3 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-blue-300 hover:text-blue-600 transition-all duration-200 flex items-center justify-center'
            >
              <PlusIcon className='w-5 h-5 mr-2' />
              Add Family Member
            </button>
          )}
        </div>

        <div className='flex justify-end gap-4 pt-6 border-t border-gray-200 animate-in slide-in-from-bottom duration-500 delay-500'>
          <button
            type='button'
            onClick={resetResidentForm}
            className='px-6 py-3 border-2 border-gray-300 rounded-lg text-gray-700 font-medium hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-all duration-200 hover:scale-105 active:scale-95'
          >
            Cancel
          </button>
          <button
            type='submit'
            disabled={isSubmitting}
            className='px-6 py-3 border-2 border-transparent rounded-lg text-white font-medium bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 transition-all duration-200 hover:scale-105 active:scale-95 relative overflow-hidden group'
          >
            <span
              className={`transition-all duration-300 ${
                isSubmitting ? 'opacity-0' : 'opacity-100'
              }`}
            >
              {selectedResident ? 'Update Resident' : 'Register Resident'}
            </span>
            {isSubmitting && (
              <span className='absolute inset-0 flex items-center justify-center'>
                <svg
                  className='animate-spin h-5 w-5 text-white'
                  xmlns='http://www.w3.org/2000/svg'
                  fill='none'
                  viewBox='0 0 24 24'
                >
                  <circle
                    className='opacity-25'
                    cx='12'
                    cy='12'
                    r='10'
                    stroke='currentColor'
                    strokeWidth='4'
                  ></circle>
                  <path
                    className='opacity-75'
                    fill='currentColor'
                    d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z'
                  ></path>
                </svg>
              </span>
            )}
            <div className='absolute inset-0 bg-white opacity-0 group-hover:opacity-10 transition-opacity duration-300'></div>
          </button>
        </div>
      </form>
    </Modal>
  )
}
